<template>
    <button
        type="button"
        class="flex -space-x-2"
        data-testid="member-avatars"
        @click="emit('click')"
    >
        <div class="relative" :key="`${member.type}-${member.id}`" v-for="member in visibleMembers">
            <sms-avatar :username="member.fullName" :src="member.avatarUrl || ''" size="lg" />

            <span
                class="absolute -bottom-0.5 -right-0.5 flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-blue-500 rounded-full"
                data-testid="lead-badge"
                v-if="member.type === ConversationMemberType.LEAD"
            >
                L
            </span>
        </div>

        <span
            class="flex items-center justify-center w-12 h-12 p-1.5 bg-white rounded-full shadow-md shrink-0 text-sm font-semibold text-gray-600"
            data-testid="overflow-indicator"
            v-if="overflowCount > 0"
        >
            +{{ overflowCount }}
        </span>
    </button>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import {
        ConversationMemberType,
        type ConversationMember,
    } from '@/hooks/useConversationMembers.ts';
    import SmsAvatar from '@/components/sms/SmsAvatar.vue';

    interface Props {
        members: ConversationMember[];
        maxVisible?: number;
    }

    interface Emits {
        (event: 'click'): void;
    }

    const emit = defineEmits<Emits>();

    const props = withDefaults(defineProps<Props>(), {
        maxVisible: 3,
    });

    const orderedMembers = computed<ConversationMember[]>(() => {
        const userMembers = props.members.filter((m) => m.type === ConversationMemberType.USER);
        const leadMember = props.members.find((m) => m.type === ConversationMemberType.LEAD);

        return [...userMembers, ...(leadMember ? [leadMember] : [])];
    });

    const visibleMembers = computed<ConversationMember[]>(() => {
        const userMembers = orderedMembers.value.filter(
            (m) => m.type === ConversationMemberType.USER
        );

        if (userMembers.length >= props.maxVisible) {
            return userMembers.slice(0, props.maxVisible);
        }

        return orderedMembers.value.slice(0, props.maxVisible);
    });

    const overflowCount = computed<number>(() =>
        Math.max(0, orderedMembers.value.length - visibleMembers.value.length)
    );
</script>
